// components/ContactSection/ContactSection.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send, 
  Clock, 
  Users, 
  Headphones,
  MessageSquare,
  Calendar,
  Globe,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Award,
  Target,
  Sparkles,
  Download,
  ExternalLink
} from 'lucide-react';
import './ContactSection.css';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    priority: 'normal',
    contactMethod: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [focusedField, setFocusedField] = useState('');
  const [visibleElements, setVisibleElements] = useState([]);
  const sectionRef = useRef(null);

  // Enhanced contact information
  const contactInfo = [
    {
      icon: <Phone size={28} />,
      title: 'Phone Support',
      details: ['+91 91016 33479', '+91 9986343766'],
      description: 'Available 24/7 for urgent support',
      availability: '24/7 Available',
      color: 'blue',
      action: 'Call Now'
    },
    {
      icon: <Mail size={28} />,
      title: 'Email Support',
      details: ['info@rotsnortheast.com', 'support@rotsnortheast.com'],
      description: 'We respond within 2 hours',
      availability: 'Response in 2hrs',
      color: 'green',
      action: 'Send Email'
    },
    {
      icon: <MapPin size={28} />,
      title: 'Visit Our Office',
      details: ['Roots of Northeast', 'Kokrajhar, Assam, India'],
      description: 'Schedule a visit to our headquarters',
      availability: 'Mon-Fri 9AM-6PM',
      color: 'purple',
      action: 'Get Directions'
    }
  ];

  // Support features
  const supportFeatures = [
    { 
      icon: <Clock size={24} />, 
      text: '24/7 Technical Support',
      description: 'Round-the-clock assistance for all your needs'
    },
    { 
      icon: <Users size={24} />, 
      text: 'Dedicated Account Manager',
      description: 'Personal point of contact for your account'
    },
    { 
      icon: <Headphones size={24} />, 
      text: 'Expert Technical Team',
      description: 'Certified professionals ready to help'
    },
    { 
      icon: <Zap size={24} />, 
      text: 'Rapid Response Time',
      description: 'Average response time under 30 minutes'
    }
  ];

  // Contact reasons/subjects
  const contactReasons = [
    { value: 'demo', label: 'Request a Demo', icon: <Target size={18} /> },
    { value: 'pricing', label: 'Pricing Information', icon: <Star size={18} /> },
    { value: 'support', label: 'Technical Support', icon: <Headphones size={18} /> },
    { value: 'partnership', label: 'Partnership Inquiry', icon: <Users size={18} /> },
    { value: 'custom', label: 'Custom Development', icon: <Globe size={18} /> },
    { value: 'consultation', label: 'Free Consultation', icon: <MessageSquare size={18} /> }
  ];

  // Success statistics
  const successStats = [
    { number: '2hrs', label: 'Avg Response Time', icon: <Clock size={20} /> },
    { number: '98%', label: 'Customer Satisfaction', icon: <Star size={20} /> },
    { number: '103+', label: 'Projects Delivered', icon: <Award size={20} /> },
    { number: '24/7', label: 'Support Available', icon: <Shield size={20} /> }
  ];

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.subject) errors.subject = 'Subject is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    else if (formData.message.trim().length < 10) errors.message = 'Message must be at least 10 characters';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target.dataset.element;
            setVisibleElements(prev => [...new Set([...prev, element])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Form submitted:', formData);
      setIsSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: '',
          priority: 'normal',
          contactMethod: 'email'
        });
        setIsSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickAction = (action, contact) => {
    console.log(`Quick action: ${action} for ${contact.title}`);
    // Add quick action logic here
  };

  return (
    <section id="contact" className="contact" ref={sectionRef}>
      {/* Animated Background */}
      <div className="contact__background">
        <div className="contact__particles">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="contact__particle"
              style={{
                '--delay': `${i * 0.4}s`,
                '--duration': `${5 + (i % 3)}s`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
      </div>

      <div className="contact__container">
        {/* Enhanced Header */}
        <div 
          className={`contact__header ${visibleElements.includes('header') ? 'contact__header--visible' : ''}`}
          data-element="header"
        >
          <div className="contact__badge">
            <MessageSquare className="contact__badge-icon" />
            <span>Let's Connect</span>
          </div>
          <h2 className="contact__title">
            <span className="contact__title-line">Ready to Transform</span>
            <span className="contact__title-gradient">Your Business?</span>
          </h2>
          <p className="contact__subtitle">
            Get in touch with our experts for personalized solutions and comprehensive 
            support. We're here to help you achieve your digital transformation goals.
          </p>
        </div>

        {/* Success Statistics */}
        <div 
          className={`contact__stats ${visibleElements.includes('stats') ? 'contact__stats--visible' : ''}`}
          data-element="stats"
        >
          <div className="contact__stats-grid">
            {successStats.map((stat, index) => (
              <div key={index} className="contact__stat">
                <div className="contact__stat-icon">
                  {stat.icon}
                </div>
                <div className="contact__stat-content">
                  <div className="contact__stat-number">{stat.number}</div>
                  <div className="contact__stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="contact__content">
          {/* Enhanced Contact Information */}
          <div 
            className={`contact__info ${visibleElements.includes('info') ? 'contact__info--visible' : ''}`}
            data-element="info"
          >
            <h3 className="contact__info-title">
              <Sparkles className="contact__info-title-icon" />
              Contact Information
            </h3>
            <p className="contact__info-description">
              Multiple ways to reach our expert team. Choose the method that works best for you.
            </p>

            <div className="contact__info-list">
              {contactInfo.map((info, index) => (
                <div key={index} className={`contact__info-item contact__info-item--${info.color}`}>
                  <div className="contact__info-icon">
                    {info.icon}
                    <div className="contact__info-icon-glow"></div>
                  </div>
                  <div className="contact__info-details">
                    <h4 className="contact__info-item-title">{info.title}</h4>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="contact__info-item-detail">{detail}</p>
                    ))}
                    <p className="contact__info-item-description">{info.description}</p>
                    <div className="contact__info-item-meta">
                      <span className="contact__info-availability">
                        <Clock size={14} />
                        {info.availability}
                      </span>
                      <button 
                        className="contact__info-action"
                        onClick={() => handleQuickAction(info.action, info)}
                      >
                        {info.action}
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Support Features */}
            <div className="contact__support-features">
              <h4 className="contact__support-title">Why Choose Our Support?</h4>
              <div className="contact__support-grid">
                {supportFeatures.map((feature, index) => (
                  <div key={index} className="contact__support-feature">
                    <div className="contact__support-feature-icon">
                      {feature.icon}
                    </div>
                    <div className="contact__support-feature-content">
                      <h5 className="contact__support-feature-title">{feature.text}</h5>
                      <p className="contact__support-feature-description">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="contact__quick-links">
              <h4 className="contact__quick-links-title">Quick Links</h4>
              <div className="contact__quick-links-grid">
                <button className="contact__quick-link">
                  <Download size={18} />
                  <span>Download Brochure</span>
                </button>
                <button className="contact__quick-link">
                  <Calendar size={18} />
                  <span>Schedule Meeting</span>
                </button>
                <button className="contact__quick-link">
                  <ExternalLink size={18} />
                  <span>View Case Studies</span>
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Contact Form */}
          <div 
            className={`contact__form-container ${visibleElements.includes('form') ? 'contact__form-container--visible' : ''}`}
            data-element="form"
          >
            <div className="contact__form-header">
              <h3 className="contact__form-title">Send us a Message</h3>
              <p className="contact__form-description">
                Fill out the form below and we'll get back to you within 2 hours during business hours.
              </p>
            </div>

            <form className="contact__form" onSubmit={handleSubmit}>
              {/* Contact Reason Selection */}
              <div className="contact__form-group contact__form-group--full">
                <label className="contact__form-label">What can we help you with?</label>
                <div className="contact__reason-grid">
                  {contactReasons.map((reason) => (
                    <button
                      key={reason.value}
                      type="button"
                      className={`contact__reason-button ${formData.subject === reason.value ? 'contact__reason-button--active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, subject: reason.value }))}
                    >
                      {reason.icon}
                      <span>{reason.label}</span>
                    </button>
                  ))}
                </div>
                {formErrors.subject && <span className="contact__form-error">{formErrors.subject}</span>}
              </div>

              {/* Personal Information */}
              <div className="contact__form-row">
                <div className="contact__form-group">
                  <label htmlFor="name" className="contact__form-label">
                    Full Name *
                    {focusedField === 'name' && <span className="contact__form-label-hint">Enter your full name</span>}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('name')}
                    onBlur={handleBlur}
                    className={`contact__form-input ${formErrors.name ? 'contact__form-input--error' : ''} ${focusedField === 'name' ? 'contact__form-input--focused' : ''}`}
                    placeholder="John Doe"
                    required
                  />
                  {formErrors.name && <span className="contact__form-error">{formErrors.name}</span>}
                </div>

                <div className="contact__form-group">
                  <label htmlFor="email" className="contact__form-label">
                    Email Address *
                    {focusedField === 'email' && <span className="contact__form-label-hint">We'll send updates here</span>}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    className={`contact__form-input ${formErrors.email ? 'contact__form-input--error' : ''} ${focusedField === 'email' ? 'contact__form-input--focused' : ''}`}
                    placeholder="john@company.com"
                    required
                  />
                  {formErrors.email && <span className="contact__form-error">{formErrors.email}</span>}
                </div>
              </div>

              <div className="contact__form-row">
                <div className="contact__form-group">
                  <label htmlFor="company" className="contact__form-label">
                    Company/Organization
                    {focusedField === 'company' && <span className="contact__form-label-hint">Optional</span>}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('company')}
                    onBlur={handleBlur}
                    className={`contact__form-input ${focusedField === 'company' ? 'contact__form-input--focused' : ''}`}
                    placeholder="Your Company Name"
                  />
                </div>

                <div className="contact__form-group">
                  <label htmlFor="phone" className="contact__form-label">
                    Phone Number
                    {focusedField === 'phone' && <span className="contact__form-label-hint">For urgent matters</span>}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('phone')}
                    onBlur={handleBlur}
                    className={`contact__form-input ${focusedField === 'phone' ? 'contact__form-input--focused' : ''}`}
                    placeholder="+91 91016 33479"
                  />
                </div>
              </div>

              {/* Priority and Contact Method */}
              <div className="contact__form-row">
                <div className="contact__form-group">
                  <label htmlFor="priority" className="contact__form-label">Priority Level</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="contact__form-select"
                  >
                    <option value="low">Low - General Inquiry</option>
                    <option value="normal">Normal - Standard Request</option>
                    <option value="high">High - Urgent Matter</option>
                    <option value="critical">Critical - Emergency</option>
                  </select>
                </div>

                <div className="contact__form-group">
                  <label htmlFor="contactMethod" className="contact__form-label">Preferred Contact Method</label>
                  <select
                    id="contactMethod"
                    name="contactMethod"
                    value={formData.contactMethod}
                    onChange={handleInputChange}
                    className="contact__form-select"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="meeting">Video Meeting</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="contact__form-group">
                <label htmlFor="message" className="contact__form-label">
                  Message *
                  {focusedField === 'message' && <span className="contact__form-label-hint">Be specific about your requirements</span>}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('message')}
                  onBlur={handleBlur}
                  rows="6"
                  className={`contact__form-textarea ${formErrors.message ? 'contact__form-textarea--error' : ''} ${focusedField === 'message' ? 'contact__form-textarea--focused' : ''}`}
                  placeholder="Tell us about your project requirements, timeline, and any specific questions you have..."
                  required
                ></textarea>
                <div className="contact__form-textarea-info">
                  <span className="contact__form-char-count">
                    {formData.message.length}/1000 characters
                  </span>
                  {formErrors.message && <span className="contact__form-error">{formErrors.message}</span>}
                </div>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className={`contact__form-submit ${isSubmitting ? 'contact__form-submit--loading' : ''} ${isSuccess ? 'contact__form-submit--success' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="contact__form-spinner"></div>
                    <span>Sending Message...</span>
                  </>
                ) : isSuccess ? (
                  <>
                    <CheckCircle className="contact__form-submit-icon" />
                    <span>Message Sent Successfully!</span>
                  </>
                ) : (
                  <>
                    <Send className="contact__form-submit-icon" />
                    <span>Send Message</span>
                  </>
                )}
              </button>

              {/* Form Footer */}
              <div className="contact__form-footer">
                <p className="contact__form-footer-text">
                  <Shield size={16} />
                  Your information is secure and will never be shared with third parties.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;